import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe ser usado dentro de AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initializeAuth = async () => {
            try {
                console.log('🔄 Iniciando verificación de autenticación...');
                const token = localStorage.getItem("control_campo_token");
                const storedUser = authService.getUser();

                console.log('📦 Token encontrado:', !!token);
                console.log('👤 Usuario en localStorage:', !!storedUser);

                if (token && storedUser) {
                    console.log('✅ Verificando token con backend...');
                    try {
                        const userData = await authService.verifyToken(token);
                        setUser(userData);
                        console.log('🎉 Autenticación exitosa');
                    } catch (verifyError) {
                        const isNetworkOrServerError = 
                            verifyError?.name === 'TypeError' || 
                            verifyError?.name === 'AbortError' || 
                            verifyError?.message?.includes('NetworkError') ||
                            verifyError?.message?.includes('Failed to fetch') ||
                            verifyError?.message?.includes('Failed to connect') ||
                            (verifyError?.status && verifyError.status !== 401 && verifyError.status !== 403);

                        if (isNetworkOrServerError) {
                            console.log('⚠️ Error de red/servidor al verificar token. Usando sesión local cacheada.');
                            setUser(storedUser);
                        } else {
                            console.error('❌ Token inválido o expirado. Cerrando sesión:', verifyError);
                            authService.logout();
                            setUser(null);
                        }
                    }
                } else {
                    console.log('❌ No hay sesión activa');
                    setUser(null);
                }
            } catch (error) {
                console.error('⚠️ Error en verificación:', error);
                authService.logout();
                setUser(null);
            } finally {
                console.log('🏁 Finalizando verificación, loading=false');
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const login = async (username, password) => {
        try {
            setLoading(true);
            console.log('🔐 [AuthContext] Starting login process...');

            const response = await authService.login(username, password);
            console.log('✅ [AuthContext] Login service returned:', response);

            // ✅ Asegurar que tanto authService como el estado se actualicen
            setUser(response.user);
            console.log('👤 [AuthContext] User state updated:', response.user);

            // ✅ VERIFICAR que realmente se guardó en localStorage
            const storedToken = localStorage.getItem("control_campo_token");
            const storedUser = authService.getUser();
            console.log('💾 [AuthContext] After login - Token in storage:', !!storedToken);
            console.log('💾 [AuthContext] After login - User in storage:', storedUser);

            return response;
        } catch (error) {
            console.error('❌ [AuthContext] Login error:', error);
            // Limpiar en caso de error
            authService.logout();
            setUser(null);
            throw error;
        } finally {
            console.log('🏁 [AuthContext] Login process completed');
            setLoading(false);
        }
    };

    const logout = () => {
        authService.logout();
        setUser(null);
    };

    const refreshUser = async () => {
        try {
            const userData = await authService.refreshUserData();
            setUser(userData);
            return userData;
        } catch (error) {
            console.error('Error refrescando datos del usuario:', error);
            logout();
            throw error;
        }
    };

    const updateUserData = (newUserData) => {
        const updatedUser = { ...user, ...newUserData };
        setUser(updatedUser);
        authService.setUser(updatedUser);
    };

    const value = {
        user,
        login,
        logout,
        loading,
        refreshUser,
        updateUserData,
        isAuthenticated: () => !!user, // ✅ Mejor usar el estado actual
        getToken: authService.getToken
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};